import { FC, PropsWithChildren, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import AdSkeleton from '@moment/components/AdSkeleton/AdSkeleton.tsx';
import './LazyAd.scss';
import { DEFAULT_AD_HEIGHT } from '@moment/shared-code/ad-utils.tsx';

export interface LazyAdProps extends PropsWithChildren {
  id: string;
  className?: string;
  onInViewChange?: (inView: boolean) => void;
  height?: number;
}

// Simple throttle function to limit the rate of execution
const throttle = (fn: (...args: any[]) => void, delay: number) => {
  let lastCall = 0;
  return (...args: any[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      fn(...args);
    }
  };
};

export const LazyAd: FC<LazyAdProps> = ({
  id,
  className,
  children,
  onInViewChange,
  height,
}) => {
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [inView, setInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasFiredRef = useRef(false);

  const checkIntersection = useCallback(() => {
    // If already in view or no ref, stop checking
    if (hasFiredRef.current || inView || !containerRef.current) return;

    const target = containerRef.current;
    const rect = target.getBoundingClientRect();

    // Logic ported from temp-neptune-vis-checker.js
    const mobileBreakpoint = 768;
    const mobileMultiple = 2;
    // [top, right, bottom, left]
    const margins = [50, 0, 50, 0];

    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;

    // Adjust for mobile
    if (windowWidth < mobileBreakpoint) {
      margins[0] *= mobileMultiple; // Top
      margins[2] *= mobileMultiple; // Bottom
    }

    const [marginTop, marginRight, marginBottom, marginLeft] = margins;

    // Calculate visibility with margins expanding the viewport
    // The element is considered "in view" if it intersects with the viewport expanded by these margins.
    const isVisible = (
      rect.bottom > -marginTop &&
      rect.top < windowHeight + marginBottom &&
      rect.right > -marginLeft &&
      rect.left < windowWidth + marginRight
    );

    if (isVisible) {
      console.debug(`[LazyAd] Ad ${id} is visible (Manual Check).`);
      hasFiredRef.current = true;
      setInView(true);
      setShowSkeleton(false);
    }
  }, [inView, id]);

  useEffect(() => {
    if (inView) return;

    const handleCheck = throttle(checkIntersection, 100);

    // Listen to window scroll/resize as we are checking against viewport
    window.addEventListener('scroll', handleCheck);
    window.addEventListener('resize', handleCheck);
    // Listen to touch and transition events for carousels (like Swiper)
    window.addEventListener('touchmove', handleCheck);
    window.addEventListener('touchend', handleCheck);
    window.addEventListener('transitionend', handleCheck);

    // Initial check
    checkIntersection();

    return () => {
      window.removeEventListener('scroll', handleCheck);
      window.removeEventListener('resize', handleCheck);
      window.removeEventListener('touchmove', handleCheck);
      window.removeEventListener('touchend', handleCheck);
      window.removeEventListener('transitionend', handleCheck);
    };
  }, [inView, checkIntersection]);

  useEffect(() => {
    if (inView) {
      onInViewChange?.(inView);
    }
  }, [inView, onInViewChange]);

  const minHeight = useMemo(() => height || DEFAULT_AD_HEIGHT, [height]);

  return (
    <div className={'lazy--ad--container'} style={{ minHeight: minHeight }}>
      {showSkeleton ? (
        <div ref={containerRef} style={{ minHeight: minHeight }}>
          <AdSkeleton height={minHeight} />
        </div>
      ) : (
        <div
          className={`lazy--ad--loaded-container ${className || ''}`}
          style={{ minHeight: minHeight }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
